/**
 * BroadcastMethods — how this service, conference or lecture goes on air.
 *
 * The same panel in church, events and school. It asks the server what is
 * actually possible rather than offering four buttons and letting one fail:
 * a method that cannot be used says why, and what would change it.
 *
 * The stream key is treated as the credential it is — masked by default,
 * copyable without ever being shown (a media desk is often on a projector),
 * re-masked the moment the panel closes, and never written to storage of any
 * kind. The server sends `no-store`, so it does not sit in a back button.
 */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Video, Smartphone, MonitorPlay, Globe, Copy, Eye, EyeOff, Loader2,
  AlertCircle, CheckCircle2, BookOpen, KeyRound, Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { BroadcastHealth } from '@/components/live/BroadcastHealth';

const ICONS = { browser: Video, phone_app: Smartphone, obs: MonitorPlay, external: Globe };

const GUIDE = {
  browser: '/help/broadcasting#browser',
  phone_app: '/help/broadcasting#phone-app',
  obs: '/help/broadcasting#obs',
  external: '/help/broadcasting#external',
};

const CredentialRow = ({ label, value, testId, masked, onToggle, onCopy }) => (
  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2">
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="truncate font-mono text-xs text-slate-800" data-testid={`${testId}-value`}>
        {value}
      </p>
    </div>
    {onToggle && (
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onToggle}
              data-testid={`${testId}-toggle`} aria-label={masked ? 'Show' : 'Hide'}>
        {masked ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      </Button>
    )}
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCopy}
            data-testid={`${testId}-copy`} aria-label="Copy">
      <Copy className="h-3.5 w-3.5" />
    </Button>
  </div>
);

export const BroadcastMethods = ({ scope, ident, onBrowserBroadcast, compact = false }) => {
  const [open, setOpen] = useState('');
  const [creds, setCreds] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['broadcast-methods', scope, ident],
    queryFn: () => api.get(`/live/${scope}/${ident}/methods`).then((r) => r.data),
    enabled: !!scope && !!ident,
  });

  // Closing the panel forgets the key. Nothing is kept in storage, so there is
  // nothing to clear beyond this.
  useEffect(() => {
    if (!open || !['phone_app', 'obs'].includes(open)) {
      setCreds(null);
      setRevealed(false);
    }
  }, [open]);

  const fetchCreds = async (reveal) => {
    setLoading(true);
    try {
      const { data: out } = await api.post(`/live/${scope}/${ident}/ingest`, null,
        { params: { reveal } });
      setCreds(out);
      setRevealed(!!reveal);
      return out;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No streaming destination could be set up');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openMethod = async (m) => {
    const next = open === m.id ? '' : m.id;
    setOpen(next);
    if (next === 'browser' && onBrowserBroadcast) onBrowserBroadcast();
    if (['phone_app', 'obs'].includes(next) && m.available) await fetchCreds(false);
  };

  const copyKey = async () => {
    // Copy without revealing: the value is fetched, written to the clipboard
    // and dropped, so a key can be pasted into OBS from a shared screen.
    const out = revealed && creds?.stream_key ? creds : await fetchCreds(true);
    if (!out?.stream_key) return;
    await navigator.clipboard?.writeText(out.stream_key);
    if (!revealed) { setCreds({ ...out, stream_key: '' }); setRevealed(false); }
    toast.success('Stream key copied');
  };

  const revoke = async () => {
    if (!window.confirm('Retire this stream key? Any encoder using it will stop.')) return;
    try {
      await api.delete(`/live/${scope}/${ident}/ingest`);
      setCreds(null); setRevealed(false);
      toast.success('Key retired');
      refetch();
    } catch {
      toast.error('That key could not be retired');
    }
  };

  if (isLoading) {
    return (
      <p className="py-3 text-xs text-slate-500" data-testid="broadcast-methods-loading">
        <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />Checking what this broadcast can use…
      </p>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-2" data-testid={`broadcast-methods-${scope}-${ident}`}>
      <BroadcastHealth scope={scope} ident={ident} />

      {!compact && (
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            How to go live
          </p>
          <Link to="/help/broadcasting" target="_blank"
                className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
                data-testid="broadcast-guide-link">
            <BookOpen className="h-3 w-3" />Setup guides
          </Link>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {data.methods.map((m) => {
          const Icon = ICONS[m.id] || Video;
          const isOpen = open === m.id;
          return (
            <button key={m.id} type="button" onClick={() => openMethod(m)}
                    className={`rounded-lg border p-2.5 text-left transition-all
                      ${isOpen ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-400'}
                      ${m.available ? '' : 'opacity-70'}`}
                    data-testid={`broadcast-method-${m.id}`}>
              <div className="flex items-start gap-2">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-700" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{m.label}</p>
                  <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{m.note}</p>
                  {!m.available && (
                    <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-amber-700"
                       data-testid={`broadcast-method-${m.id}-reason`}>
                      <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />{m.reason}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {['phone_app', 'obs'].includes(open) && (
        <div className="space-y-2 rounded-lg border border-slate-300 bg-slate-50 p-2.5"
             data-testid="broadcast-credentials">
          {loading && !creds ? (
            <p className="text-xs text-slate-500">
              <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />Setting up the destination…
            </p>
          ) : creds ? (
            <>
              <div className="flex items-center gap-2">
                <KeyRound className="h-3.5 w-3.5 text-slate-500" />
                <p className="text-xs font-medium text-slate-700">
                  {open === 'obs' ? 'OBS Studio settings' : 'Phone app settings'}
                </p>
                <Badge variant="outline" className="ml-auto text-[10px]">
                  {creds.plays_on_fwm
                    ? <><CheckCircle2 className="mr-1 h-3 w-3 text-emerald-600" />plays on FWM</>
                    : 'external provider'}
                </Badge>
              </div>
              <CredentialRow label="Server" value={creds.server_url} testId="rtmp-server"
                             onCopy={() => {
                               navigator.clipboard?.writeText(creds.server_url);
                               toast.success('Server address copied');
                             }} />
              <CredentialRow label="Stream key"
                             value={revealed && creds.stream_key ? creds.stream_key : creds.masked_key}
                             testId="rtmp-key" masked={!revealed}
                             onToggle={async () => {
                               if (revealed) { setCreds({ ...creds, stream_key: '' }); setRevealed(false); }
                               else await fetchCreds(true);
                             }}
                             onCopy={copyKey} />
              <p className="text-[11px] leading-relaxed text-slate-500">{creds.note}</p>
              <div className="flex items-center gap-2">
                <Link to={GUIDE[open]} target="_blank"
                      className="text-[11px] font-medium text-slate-700 underline">
                  {open === 'obs' ? 'How to set up OBS' : 'How to set up the phone app'}
                </Link>
                {creds.kind === 'livekit' && (
                  <Button variant="ghost" size="sm"
                          className="ml-auto h-7 text-[11px] text-rose-600"
                          onClick={revoke} data-testid="rtmp-revoke">
                    <Trash2 className="mr-1 h-3 w-3" />Retire key
                  </Button>
                )}
              </div>
            </>
          ) : (
            <p className="text-xs text-amber-700" data-testid="broadcast-credentials-none">
              No streaming destination configured. Add a valid RTMP server URL and
              stream key before connecting OBS or a mobile broadcasting application.
            </p>
          )}
        </div>
      )}

      {open === 'external' && (
        <p className="rounded-lg border border-slate-200 bg-white p-2.5 text-[11px] leading-relaxed text-slate-600"
           data-testid="broadcast-external-note">
          Stream to YouTube Live, Facebook or Restream from OBS or a phone app using
          <em> their</em> server and key, then paste the public watch link into this
          broadcast. The FWM page will show their player, and the replay stays with
          them — which is how recordings are kept today.
        </p>
      )}
    </div>
  );
};

export default BroadcastMethods;
